import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { STATS, MEMORY_CARDS } from '../data/staticContent';
import HomeBanner from '../components/home/HomeBanner';
import HomeAboutPreview from '../components/home/HomeAboutPreview';
import HomeActivities from '../components/home/HomeActivities';

/* ── Stats Strip ─────────────────────────────── */
const StatsStrip = () => {
  const { t } = useLanguage();
  return (
    <div className="stats-strip">
      <div className="container">
        <div className="d-flex flex-wrap justify-content-around align-items-center">
          {STATS.map(({ num, key }, i) => (
            <React.Fragment key={key}>
              {i > 0 && <div className="stat-divider d-none d-md-block" style={{height:'40px'}}></div>}
              <div className="text-center px-3 py-2">
                <span className="stat-num">{num}</span>
                <span className="stat-label">{t(key)}</span>
              </div>
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ── Quote Block ─────────────────────────────── */
const QuoteBlock = () => {
  const { t } = useLanguage();
  return (
    <section className="quote-section">
      <div className="container position-relative" style={{zIndex:1}}>
        <div className="text-center">
          <i className="bi bi-quote d-block mb-3" style={{fontSize:'2.5rem',color:'rgba(253,246,236,0.18)'}}></i>
          <blockquote className="quote-text">{t('home.quote')}</blockquote>
          <p className="quote-author mt-3">{t('home.quoteAuthor')}</p>
        </div>
      </div>
    </section>
  );
};

/* ── Story Section ───────────────────────────── */
const FoundationStory = () => {
  const { t } = useLanguage();
  return (
    <section className="section-pad-alt">
      <div className="container">
        <div className="row align-items-center g-5">
          <div className="col-lg-6">
            <span className="eyebrow"><i className="bi bi-journal-heart me-2"></i>Our Story</span>
            <h2 className="section-title">{t('home.storyTitle')}</h2>
            <div className="divider-brand"></div>
            <p className="body-text">{t('home.storyText')}</p>
            <div className="d-flex flex-wrap gap-3 mt-4">
              <Link to="/about" className="btn-brand">{t('common.learnMore')} <i className="bi bi-arrow-right"></i></Link>
              <Link to="/who-is-chaitralee" className="btn-accent">{t('nav.whoIsChaitralee')} <i className="bi bi-arrow-right"></i></Link>
            </div>
          </div>
          <div className="col-lg-6">
            <div className="row g-3">
              {[
                { icon:'bi-flower1',      text:'5 March 2005',       sub:'Birthday / जन्मदिवस',   cls:'card-icon-primary' },
                { icon:'bi-heart-fill',   text:'19 Years',           sub:'Of Beautiful Life / जगण्याची',cls:'card-icon-secondary' },
                { icon:'bi-book-half',    text:'She Is...',          sub:'A tribute book / श्रद्धांजली', cls:'card-icon-accent' },
                { icon:'bi-star-fill',    text:'IAS Aspirant',       sub:'Her dream / तिचे स्वप्न',  cls:'card-icon-purple' },
              ].map(({ icon, text, sub, cls }) => (
                <div className="col-6" key={text}>
                  <div className="highlight-stat-box">
                    <div className={'card-icon-wrap ' + cls + ' mx-auto mb-2'} style={{width:'44px',height:'44px',borderRadius:'10px'}}>
                      <i className={'bi ' + icon}></i>
                    </div>
                    <span className="highlight-stat-num" style={{fontSize:'1.3rem'}}>{text}</span>
                    <span className="highlight-stat-label">{sub}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── Tributes Section ────────────────────────── */
const TributesSection = () => {
  const { t } = useLanguage();
  const tributes = [
    { tKey:'t1', avatarBg:'#8B2252', initial:'सं' },
    { tKey:'t2', avatarBg:'#D4893A', initial:'नि' },
    { tKey:'t3', avatarBg:'#6E9E7A', initial:'अ' },
    { tKey:'t4', avatarBg:'#7B5EA7', initial:'प्र' },
    { tKey:'t5', avatarBg:'#C9921A', initial:'अ' },
    { tKey:'t6', avatarBg:'#E06C75', initial:'प्र' },
  ];
  return (
    <section className="section-pad">
      <div className="container">
        <div className="text-center mb-5">
          <span className="eyebrow"><i className="bi bi-chat-quote me-2"></i>Tributes</span>
          <h2 className="section-title">{t('tributes.title')}</h2>
          <div className="divider-brand-center"></div>
          <p className="section-subtitle">{t('tributes.subtitle')}</p>
        </div>
        <div className="row g-4">
          {tributes.map(({ tKey, avatarBg, initial }, i) => (
            <div className="col-lg-4 col-md-6" key={tKey}>
              <div className={'tribute-card anim-fade-up anim-delay-' + (i+1)}>
                <div className="tribute-quote-mark">"</div>
                <p className="tribute-text">{t('tributes.' + tKey + '_text')}</p>
                <div className="tribute-author">
                  <div className="tribute-avatar" style={{background: avatarBg}}>{initial}</div>
                  <div>
                    <div className="tribute-author-name">{t('tributes.' + tKey + '_name')}</div>
                    <div className="tribute-author-role">{t('tributes.' + tKey + '_role')}</div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="text-center mt-4">
          <Link to="/who-is-chaitralee" className="btn-accent">
            {t('common.learnMore')} <i className="bi bi-arrow-right"></i>
          </Link>
        </div>
      </div>
    </section>
  );
};

/* ── Book Promo ──────────────────────────────── */
const BookPromo = () => {
  const { t } = useLanguage();
  return (
    <section className="section-pad-alt">
      <div className="container">
        <div className="row align-items-center g-5 p-lg-4 p-3 rounded-4 bg-card border border-site">
          <div className="col-lg-4 col-md-5 text-center">
            <div className="book-cover-mockup">
              <div className="book-cover-inner">
                <div className="book-title-text">She Is...</div>
                <div className="book-title-line"></div>
                <div className="book-subtitle-text">A tribute to Chaitralee Indore</div>
                <i className="bi bi-book mt-3" style={{fontSize:'2rem',color:'rgba(253,246,236,0.18)'}}></i>
              </div>
            </div>
          </div>
          <div className="col-lg-8 col-md-7">
            <span className="eyebrow"><i className="bi bi-book me-2"></i>New Publication</span>
            <h2 className="section-title">{t('home.bookTitle')}</h2>
            <div className="divider-brand"></div>
            <p className="body-text">{t('home.bookText')}</p>
            <div className="d-flex flex-wrap gap-3">
              <a href="https://www.chaitraleefoundationpune.com" target="_blank" rel="noopener noreferrer" className="btn-brand">
                <i className="bi bi-globe"></i> {t('home.bookCta')}
              </a>
              <Link to="/about" className="btn-accent">{t('common.learnMore')} <i className="bi bi-arrow-right"></i></Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── Memory Strip ────────────────────────────── */
const MemoryStrip = () => {
  const { t } = useLanguage();
  return (
    <section className="memory-strip">
      <div className="container">
        <h2 className="section-title text-center mb-1">{t('home.memoryTitle')}</h2>
        <div className="divider-brand-center mb-4"></div>
        <div className="memory-scroll-row">
          {MEMORY_CARDS.map(({ key, icon, cardClass }) => (
            <div key={key} className={cardClass}>
              <i className={'bi ' + icon + ' memory-icon'}></i>
              <span className="memory-label">{t('home.' + key)}</span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── CTA Banner ──────────────────────────────── */
const CTABanner = () => {
  const { t } = useLanguage();
  return (
    <section className="cta-section">
      <div className="cta-decor cta-decor-1"></div>
      <div className="cta-decor cta-decor-2"></div>
      <div className="container text-center position-relative" style={{zIndex:1}}>
        <h2 className="cta-title">{t('home.ctaTitle')}</h2>
        <p className="cta-text">{t('home.ctaText')}</p>
        <div className="d-flex flex-wrap gap-3 justify-content-center">
          <Link to="/you-can" className="btn-white"><i className="bi bi-heart-fill"></i> {t('home.ctaDonate')}</Link>
          <Link to="/contact" className="btn-brand-outline"><i className="bi bi-envelope"></i> {t('home.ctaContact')}</Link>
        </div>
      </div>
    </section>
  );
};

/* ── Main Home ───────────────────────────────── */
const Home = () => (
  <>
    <HomeBanner />
    <StatsStrip />
    <HomeAboutPreview />
    <QuoteBlock />
    <HomeActivities />
    <FoundationStory />
    <TributesSection />
    <BookPromo />
    <MemoryStrip />
    <CTABanner />
  </>
);

export default Home;
